import axios from "axios";
import api from "./api";

const { LOGWORK, BACKLOG } = api;

/**
 * Logwork Service - handles all API calls for logwork functionality
 */
const LogworkService = {
  /**
   * Get list of projects available for current user
   * @param {Object} params - Query parameters
   * @param {Boolean} params.include_done_project - Include done projects
   * @returns {Promise}
   */
  getProjects(params = {}) {
    return axios.get(LOGWORK.PROJECTS, { params });
  },

  /**
   * Get list of members for projects
   * @param {Object} params - Query parameters
   * @param {String} params.project_ids - Comma-separated project IDs (optional)
   * @returns {Promise}
   */
  getMembers(params = {}) {
    return axios.get(LOGWORK.MEMBERS, { params });
  },

  /**
   * Get logwork timeline master rows (grouped data)
   * @param {Object} params - Query parameters
   * @param {String} params.view - View mode: day, week, month
   * @param {String} params.from_date - Start date (yyyy-mm-dd)
   * @param {String} params.to_date - End date (yyyy-mm-dd)
   * @param {String} params.group_by - Group by: project, member
   * @param {String} params.member_ids - Comma-separated member IDs (if group_by=member)
   * @param {String} params.project_ids - Comma-separated project IDs (if group_by=project)
   * @param {String} params.item_type - Item type: summary, issues, member, project
   * @param {Number} params.page - Page number
   * @param {Number} params.per_page - Items per page
   * @returns {Promise}
   */
  getTimeline(params = {}, cancelToken = null) {
    return axios.get(LOGWORK.TIMELINE, { params, cancelToken });
  },

  /**
   * Get logwork timeline detail rows (child items)
   * @param {String|Number} id - Parent row ID
   * @param {Object} params - Query parameters
   * @param {String} params.view - View mode: day, week, month
   * @param {String} params.from_date - Start date (yyyy-mm-dd)
   * @param {String} params.to_date - End date (yyyy-mm-dd)
   * @param {String} params.group_by - Group by: project, member
   * @param {String} params.member_ids - Comma-separated member IDs
   * @param {String} params.project_ids - Comma-separated project IDs
   * @param {String} params.item_type - Item type: issues, member, project
   * @param {Number} params.page - Page number
   * @param {Number} params.per_page - Items per page
   * @returns {Promise}
   */
  getTimelineItems(id, params = {}) {
    const url = LOGWORK.TIMELINE_ITEMS.replace("{id}", id);
    return axios.get(url, { params });
  },

  /**
   * Get list of weeks and their submit status
   * @returns {Promise}
   */
  getSubmitWeeks() {
    return axios.get(LOGWORK.SUBMIT);
  },

  /**
   * Get member summary logwork hours for a specific week
   * @param {Object} params - Query parameters
   * @param {Number|String} params.project_id - Project ID
   * @param {Number|String} params.week_number - Week number
   * @returns {Promise}
   */
  getSubmitSummary(params = {}) {
    return axios.get(LOGWORK.SUBMIT_SUMMARY, { params });
  },

  /**
   * Submit logwork for a week
   * @param {Object} data - Submit data
   * @param {Number|String} data.project_id - Project ID
   * @param {Number|String} data.week_number - Week number
   * @returns {Promise}
   */
  submitLogwork(data = {}) {
    return axios.post(LOGWORK.SUBMIT, data);
  },

  /**
   * Create a new logwork entry
   * @param {Object} data - Logwork data
   * @param {String} data.date - Date (yyyy-mm-dd)
   * @param {Number|String} data.project_id - Project ID
   * @param {Number|String} data.issue_id - Issue ID
   * @param {String} data.issue_summary - Issue summary
   * @param {Number|String} data.attribute_id - Attribute ID
   * @param {Number} data.spent_time - Spent time in hours
   * @param {String} data.note - Note/comment
   * @returns {Promise}
   */
  createLogwork(data = {}) {
    return axios.post(LOGWORK.CREATE, data);
  },

  /**
   * Update an existing logwork entry
   * @param {Number|String} id - Logwork ID
   * @param {Object} data - Logwork data
   * @param {String} data.date - Date (yyyy-mm-dd)
   * @param {Number|String} data.project_id - Project ID
   * @param {Number|String} data.issue_id - Issue ID
   * @param {String} data.issue_summary - Issue summary
   * @param {Number|String} data.attribute_id - Attribute ID
   * @param {Number} data.spent_time - Spent time in hours
   * @param {String} data.note - Note/comment
   * @returns {Promise}
   */
  updateLogwork(id, data = {}) {
    const url = LOGWORK.UPDATE.replace("{id}", id);
    return axios.put(url, data);
  },

  /**
   * Get logwork entry details
   * @param {Number|String} id - Logwork ID
   * @returns {Promise}
   */
  getLogworkDetail(id) {
    const url = LOGWORK.DETAIL.replace("{id}", id);
    return axios.get(url);
  },

  /**
   * Get list of submitted logwork for approval
   * @param {Object} params - Query parameters
   * @param {Number} params.week_number - Week number (optional)
   * @param {Number} params.year - Year (optional)
   * @param {String} params.from_date - From date (yyyy-mm-dd, should be Monday)
   * @param {String} params.to_date - To date (yyyy-mm-dd, should be Sunday)
   * @returns {Promise}
   */
  getApprovalList(params = {}) {
    return axios.get(LOGWORK.APPROVAL, { params });
  },

  /**
   * Approve or reject logwork submission
   * @param {Object} data - Approval data
   * @param {Number|String} data.submit_id - Submit ID
   * @param {String} data.approval_status - approve or reject
   * @param {String} data.comment - Comment from PM
   * @returns {Promise}
   */
  approveLogwork(data = {}) {
    return axios.post(LOGWORK.APPROVAL, data);
  },

  /**
   * Export logwork entries to Excel
   * @param {Object} params - Query parameters
   * @param {String} params.from_date - From date (yyyy-mm-dd)
   * @param {String} params.to_date - To date (yyyy-mm-dd)
   * @returns {Promise} - Blob data for Excel file
   */
  exportLogwork(params = {}) {
    return axios.get(LOGWORK.EXPORT, {
      params,
      responseType: "blob"
    });
  },

  /**
   * Get Backlog issue information
   * @param {Object} params - Query parameters
   * @param {String} params.project_key - Project key in Backlog
   * @param {String} params.issue_key - Issue key in Backlog
   * @returns {Promise}
   */
  getBacklogIssue(params = {}) {
    return axios.get(BACKLOG.ISSUE, { params });
  },

  /**
   * Get logwork attributes list
   * @returns {Promise}
   */
  getAttributes() {
    return axios.get(LOGWORK.ATTRIBUTES);
  }
};

export default LogworkService;
